<template>
    <div class="relative">
        <div class="mb-6 flex items-center justify-between">
            <div class="space-y-2">
                <ULink to="/invoices/providers" class="flex items-center space-x-1 text-sm font-semibold text-gray-500 hover:text-gray-700 py-1">
                    <span>Provider Invoices</span>
                    <UIcon name="i-ph-caret-right-bold" class="size-2.5" />
                </ULink>
                <div v-if="invoice" class="flex items-center space-x-4">
                    <h1 class="text-3xl font-semibold text-gray-700">Invoice #{{ invoice.number.toString().padStart(6, '0') }}</h1>
                    <BadgeInvoiceStatus :invoice="invoice as any" />
                </div>
            </div>

            <div class="flex items-center gap-x-2">
                <template v-if="!invoice?.settled_at && !invoice?.voided_at">
                    <UButton
                        @click="overlays.settle = true"
                        label="Settle"
                        color="emerald"
                        icon="i-ph-check-fill"
                    />
                    <UButton
                        @click="overlays.void = true"
                        label="Void"
                        color="red"
                        icon="i-ph-x-fill"
                    />
                </template>
            </div>
        </div>

        <div v-if="invoice" class="relative flex items-start gap-4">
            <div class="sticky top-0 flex-shrink-0 w-96 space-y-4">
                <div class="relative bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Details</h3>
                    </div>
                    <div class="px-6 py-5 space-y-4">
                        <Field label="ID">
                            <span class="font-mono">{{ invoice.id.split('-').at(-1)?.toUpperCase() }}</span>
                        </Field>
                        <Field v-if="invoice.provider" label="Provider">
                            <div class="flex items-center space-x-2.5 mt-1.5">
                                <Avatar bucket="media" folder="providers" size="sm" :placeholder="invoice.provider.name" :path="invoice.provider.avatar" />
                                <span>{{ invoice.provider.name }}</span>
                            </div>
                        </Field>
                        <div class="grid grid-cols-2 gap-2">
                            <Field label="Transport Count">
                                <span class="font-mono">{{ invoice.transports?.length || '–' }}</span>
                            </Field>
                            <Field label="Commission">
                                <span class="font-mono">{{ invoice.commission_percentage }}%</span>
                            </Field>
                        </div>
                        <div class="grid grid-cols-2 gap-2">
                            <Field label="Gross Total">
                                <span class="font-mono">
                                    ${{ grossTotal.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                                </span>
                            </Field>
                            <Field label="Commission Total">
                                <span class="font-mono">
                                    ${{ commissionTotal.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                                </span>
                            </Field>
                        </div>
                        <Field label="Created">
                            <span>{{ dayjs(invoice.created_at).format('MMM D, YYYY HH:mm') }}</span>
                        </Field>
                        <Field v-if="invoice.settled_at" label="Settled On">
                            <span>{{ dayjs(invoice.settled_at).format('MMM D, YYYY HH:mm') }}</span>
                        </Field>
                        <Field v-if="invoice.voided_at" label="Voided On">
                            <span>{{ dayjs(invoice.voided_at).format('MMM D, YYYY HH:mm') }}</span>
                        </Field>
                    </div>
                </div>
            </div>
            <div class="flex-1">
                <div class="flex flex-col bg-white ring-1 ring-black/5 rounded-lg shadow-sm">
                    <div class="flex items-center px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Transports</h3>
                    </div>

                    <UTable
                        :rows="paginatedTransports"
                        :columns="tableColumns"
                        :loading="loading"
                        :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                        :empty-state="{ icon: 'i-ph-ambulance-duotone', label: 'No transports.' }"
                        :ui="{ th: { padding: 'whitespace-nowrap' }, td: { padding: 'py-3' } }"
                    >
                        <template #number-data="{ row }">
                            <ULink :to="`/transports/${row.id}`" class="font-mono hover:underline">{{ row.number?.toString().padStart(6, '0') }}</ULink>
                        </template>
                        <template #customer-data="{ row }">
                            <p class="text-sm">
                                {{ [row.customer?.first_name, row.customer?.middle_name, row.customer?.last_name].filter(Boolean).join(' ') }}
                            </p>
                        </template>
                        <template #scheduled-data="{ row }">
                            <TableDateCell :date="row.scheduled_date" without-time class="text-sm" />
                        </template>
                        <template #price-data="{ row }">
                            <p class="font-mono text-sm text-right">${{ row.price ?? 0 }}</p>
                        </template>
                        <template #commission-data="{ row }">
                            <p class="font-mono text-sm text-right">
                                ${{ ((row.price || 0) * invoice.commission_percentage / 100).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                            </p>
                        </template>
                    </UTable>
                </div>

                <TablePagination :pagination="pagination" class="shrink-0 mt-4" />
            </div>
        </div>

        <OverlayInvoiceSettleProviderInvoice v-model:open="overlays.settle" @reload="fetchData" :invoice="invoice" />
        <OverlayInvoiceVoidProviderInvoice v-model:open="overlays.void" @reload="fetchData" :invoice="invoice" />
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import type { ProviderInvoice } from '~/types/invoice'
import { UserRole } from '~/types/user'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin] })
useHead({ title: 'View Provider Invoice' })

const route = useRoute()
const client = useSupabaseClient()
const pagination = usePagination()

const loading = ref(false)
const invoice = ref<ProviderInvoice>()

const overlays = reactive({
    settle: false,
    void: false,
})

const grossTotal = computed(() => {
    if (!invoice.value?.transports) return 0
    return invoice.value.transports.reduce((sum, t: any) => sum + (t.price || 0), 0)
})

const commissionTotal = computed(() => {
    if (!invoice.value) return 0
    return grossTotal.value * invoice.value.commission_percentage / 100
})

const paginatedTransports = computed(() => {
    const start = pagination.page * pagination.limit
    const end = (pagination.page + 1) * pagination.limit
    return invoice.value?.transports?.slice(start, end) || []
})

const tableColumns = [
    { key: 'number', label: '#' },
    { key: 'customer', label: 'Patient', class: 'w-full' },
    { key: 'scheduled', label: 'Date' },
    { key: 'price', label: 'Price' },
    { key: 'commission', label: 'Commission' },
]

onMounted(() => fetchData())

async function fetchData() {
    loading.value = true

    const { data, error } = await client
        .from('provider_invoices')
        .select(`
            id, number, provider_id, commission_percentage, settled_at, voided_at, created_at, updated_at,
            provider:providers (id, name, avatar),
            transports (id, number, price, scheduled_date, customer:customers (first_name, middle_name, last_name))
        `)
        .eq('id', route.params.id)
        .single()

    if (data) {
        invoice.value = data as unknown as ProviderInvoice
        pagination.setTotal(invoice.value.transports?.length || 0)
    } else if (error) {
        console.error(error)
    }

    loading.value = false
}
</script>
